import { useState, useEffect } from "react"
import { shape, bool, string, array } from "prop-types"
import EventHeader from "./EventHeader"
import UnfinalizedAttendees from "./UnfinalizedAttendees"
import FilterBar from "components/admin/FilterBar"
import { ATTENDANCE_KIND } from "components/admin/FilterBar/AttendanceOptions"
import { ASSIGNMENT_KIND } from "components/admin/FilterBar/AssignmentOptions"
import RegistrationsTable from "./RegistrationsTable"
import { SideNav, Paginate } from "components/ui"
import { createRegistrationPaths, splitUrlParams } from "./eventHelpers"
import { api } from "utils/api"
import AttendanceReportName from "components/admin/Event/AttendanceReportName"
import { snakeToCamelKeys } from "utils/helpers"
import useFeature from "hooks/use_feature"
import useFlipperFlag from "hooks/use_flipper_flag"

const Event = ({
  event,
  apiBaseUrl,
  checkInsBaseUrl,
  isActingAsStaff,
  unfinalizedAttendees,
  filterOptions,
  subscribedToCheckIns,
  checkInsSettingsPath
}) => {
  const [baseParams, setBaseParams] = useState({})
  const [filterParams, setFilterParams] = useState({})
  const [attendees, setAttendees] = useState([])
  const [attendeeCounts, setAttendeeCounts] = useState({})
  const [pagination, setPagination] = useState({})
  const [isFetching, setIsFetching] = useState(true)

  const { enabled: unifiedSignupsEnabled } = useFeature("unified_detailed_signups_ccw", event.id)
  const detailedWaitlistFlipperEnabled = useFlipperFlag("ROLLOUT_detailed_waitlist")
  const showWaitlist = event.isSimple
    ? true
    : detailedWaitlistFlipperEnabled && unifiedSignupsEnabled

  useEffect(() => {
    const { params, searches } = splitUrlParams(window.location.search)

    setFilterParams(searches)
    setBaseParams(params)

    fetchRegistrations(params, searches)
  }, [])

  const fetchRegistrations = async (params, searches) => {
    const paths = {
      url: event.paths.show,
      request: event.paths.search
    }

    const { requestPath, urlPath } = createRegistrationPaths(paths, params, searches)

    const response = await api.get(requestPath)

    setAttendees(snakeToCamelKeys(response.attendees))
    setAttendeeCounts(attendeeCounts => ({
      ...attendeeCounts,
      ...response.attendeeCounts
    }))
    setPagination(response.pagination)
    setBaseParams(params)
    setIsFetching(false)

    history.replaceState({}, null, urlPath)
    return response
  }

  const handleNavClick = (param = null) => {
    setIsFetching(true)
    const newParams = baseParams

    delete newParams.show_canceled
    delete newParams.show_waitlist
    delete newParams.show_attendance
    delete newParams.attendance_report_id
    delete newParams.page

    const newBaseParams = { ...newParams, ...param }

    setBaseParams(newBaseParams)

    return fetchRegistrations(newBaseParams, filterParams)
  }

  const handleSortClick = ({ sortBy, sortDir }) => {
    return fetchRegistrations(
      { ...baseParams, page: 1, sort_by: sortBy, sort_dir: sortDir },
      filterParams
    )
  }

  const handleFilterClear = () => {
    setIsFetching(true)
    setFilterParams({})

    return fetchRegistrations(baseParams, {})
  }

  const handleFilterRemove = key => {
    setIsFetching(true)

    const newParams = Object.assign({}, filterParams)
    delete newParams[key]

    setFilterParams(newParams)
    return fetchRegistrations(baseParams, newParams)
  }

  const handleFilterAdd = newFilter => {
    setIsFetching(true)

    const entries = Object.values(filterParams).filter(x => x.kind === newFilter.kind).length

    const key = newFilter.key
      ? newFilter.key
      : entries > 0
      ? `${newFilter.kind}${Math.floor(Math.random() * 100) + 1}`
      : newFilter.kind

    const newSearchParams = {
      ...filterParams,
      [key]: newFilter
    }

    const { page: _page, ...newBaseParams } = baseParams

    setFilterParams(newSearchParams)
    setBaseParams(newBaseParams)

    return fetchRegistrations(newBaseParams, newSearchParams)
  }

  let navItems = [
    {
      text: "Active",
      subText: attendeeCounts && attendeeCounts.activeCount,
      type: "a",
      onClick: () => handleNavClick(),
      isCurrent:
        !baseParams.show_canceled && !baseParams.show_waitlist && !baseParams.show_attendance
    },
    {
      text: "Canceled",
      subText: attendeeCounts && attendeeCounts.canceledCount,
      type: "a",
      onClick: () => handleNavClick({ show_canceled: true }),
      isCurrent: baseParams.show_canceled
    }
  ]

  if (showWaitlist) {
    navItems.push({
      text: "Waitlist",
      subText: attendeeCounts && attendeeCounts.waitlistCount,
      type: "a",
      onClick: () => handleNavClick({ show_waitlist: true }),
      isCurrent: baseParams.show_waitlist
    })
  }

  if (filterOptions[ATTENDANCE_KIND].length > 0) {
    navItems = [
      ...navItems,
      { text: "Attendance", type: "header" },
      ...filterOptions[ATTENDANCE_KIND].map(ar => ({
        key: ar.id,
        text: <AttendanceReportName attendanceReport={ar} />,
        type: "a",
        onClick: () =>
          handleNavClick({
            show_attendance: true,
            attendance_report_id: ar.id
          }),
        isCurrent:
          baseParams.show_attendance &&
          baseParams.attendance_report_id.toString() == ar.id.toString()
      }))
    ]
  }

  let attendeeCount = attendeeCounts.activeCount

  if (baseParams.show_canceled) {
    attendeeCount = attendeeCounts.canceledCount
  } else if (baseParams.show_waitlist) {
    attendeeCount = attendeeCounts.waitlistCount
  } else if (baseParams.show_attendance) {
    attendeeCount = attendeeCounts.attendanceCount
  }

  return (
    <div className="d-f@md">
      <SideNav navItems={{ attendees: navItems }}></SideNav>
      <div className="f-1 o-h">
        <div
          className="pane"
          css={{
            "--pane--border-radius": "3px 3px 0 0",
            "--pane--padding": 0,
            "--pane--box-shadow-color": "transparent"
          }}
        >
          {!baseParams.show_attendance && (
            <FilterBar
              event={event}
              filterOptions={filterOptions}
              searchFilters={filterParams}
              onFilterAdd={handleFilterAdd}
              onFilterRemove={handleFilterRemove}
              onFilterClear={handleFilterClear}
              waitlist={baseParams.show_waitlist}
            />
          )}

          <EventHeader
            event={event}
            attendeeCount={attendeeCount}
            baseParams={baseParams}
            filterParams={filterParams}
            assignmentTypes={filterOptions[ASSIGNMENT_KIND]}
            apiBaseUrl={apiBaseUrl}
            checkInsBaseUrl={checkInsBaseUrl}
            isActingAsStaff={isActingAsStaff}
            subscribedToCheckIns={subscribedToCheckIns}
            checkInsSettingsPath={checkInsSettingsPath}
          />

          {isActingAsStaff && unfinalizedAttendees.length > 0 && (
            <UnfinalizedAttendees attendees={unfinalizedAttendees} />
          )}

          <RegistrationsTable
            event={event}
            attendees={attendees}
            isFetching={isFetching}
            onSortClick={handleSortClick}
            waitlist={baseParams.show_waitlist}
          />

          {!isFetching && (
            <div className="pagination-wrapper">
              <Paginate
                pagination={pagination}
                onNavClick={handleNavClick}
                baseParams={baseParams}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

Event.propTypes = {
  event: shape({
    isSimple: bool,
    paths: shape({
      show: string,
      search: string
    })
  }),
  apiBaseUrl: string,
  checkInsBaseUrl: string,
  isActingAsStaff: bool,
  unfinalizedAttendees: array,
  filterOptions: shape({
    attendanceReport: array,
    assignment: array
  }),
  subscribedToCheckIns: bool,
  checkInsSettingsPath: string
}

export default Event
